import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { createCircuitGroup, assignFixtureToCircuit } from '@/services/floorPlanService';
import { toast } from 'sonner';
import { Circuit, LightingFixture } from '@/types/floorPlanViewer';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/lib/logger';

interface CreateCircuitWithFixturesParams {
  floorPlanId: string;
  name: string;
  fixtureIds: string[];
  loadType?: string;
  zone?: string;
}

export function useOptimisticCircuitMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createCircuitWithFixtures'],
    mutationFn: async ({ floorPlanId, name, fixtureIds, loadType = 'lighting', zone = '' }: CreateCircuitWithFixturesParams) => {
      // Create the circuit
      const { data: newCircuit, error } = await createCircuitGroup(
        floorPlanId,
        name.trim(),
        loadType,
        zone
      );
      
      if (error || !newCircuit) {
        throw error || new Error('Failed to create load');
      }
      
      // Assign fixtures to the circuit in parallel for better performance
      if (fixtureIds.length > 0) {
        await Promise.all(
          fixtureIds.map(fixtureId => assignFixtureToCircuit(fixtureId, newCircuit.id))
        );
      }
      
      return { circuit: newCircuit, fixtureIds };
    },
    
    onMutate: async ({ floorPlanId, name, fixtureIds, loadType = 'lighting', zone = '' }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.entitiesByFloorPlan('circuits', floorPlanId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.entitiesByFloorPlan('lighting_fixtures', floorPlanId) });
      
      // Snapshot current data
      const previousCircuits = queryClient.getQueryData<Circuit[]>(
        queryKeys.entitiesByFloorPlan('circuits', floorPlanId)
      );
      const previousFixtures = queryClient.getQueryData<LightingFixture[]>(
        queryKeys.entitiesByFloorPlan('lighting_fixtures', floorPlanId)
      );
      
      // Calculate next sequence number considering optimistic circuits
      const existingSequenceNumbers = previousCircuits?.map(c => c.sequence_number || 0) || [];
      const maxSequence = existingSequenceNumbers.length > 0 ? Math.max(...existingSequenceNumbers) : 0;
      
      // Create optimistic circuit
      const tempId = `temp-${uuidv4()}`;
      const optimisticCircuit: Circuit = {
        id: tempId, // Temporary ID
        floor_plan_id: floorPlanId,
        name,
        load_type: loadType,
        zone,
        sequence_number: maxSequence + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        notes: null,
        panel_id: null,
        panel_slot: null,
        parent_circuit_id: null,
        _optimistic: true // Mark as optimistic
      };
      
      // Optimistically update circuits
      if (previousCircuits) {
        queryClient.setQueryData<Circuit[]>(
          queryKeys.entitiesByFloorPlan('circuits', floorPlanId),
          [...previousCircuits, optimisticCircuit]
        );
      }
      
      // Optimistically update fixtures to show circuit assignment
      if (previousFixtures && fixtureIds.length > 0) {
        const updatedFixtures = previousFixtures.map(fixture => 
          fixtureIds.includes(fixture.id) 
            ? { ...fixture, circuit_id: optimisticCircuit.id }
            : fixture
        );
        queryClient.setQueryData<LightingFixture[]>(
          queryKeys.entitiesByFloorPlan('lighting_fixtures', floorPlanId),
          updatedFixtures
        );
      }
      
      logger.log('Optimistic update applied for circuit creation');
      
      // Return context for rollback
      return { previousCircuits, previousFixtures, optimisticCircuit };
    },
    
    onError: (error, variables, context) => {
      logger.error('Failed to create circuit:', error);
      
      // Rollback on error
      if (context) {
        queryClient.setQueryData(
          queryKeys.entitiesByFloorPlan('circuits', variables.floorPlanId),
          context.previousCircuits
        );
        queryClient.setQueryData(
          queryKeys.entitiesByFloorPlan('lighting_fixtures', variables.floorPlanId),
          context.previousFixtures
        );
      }
      
      toast.error('Failed to create load. Please try again.');
    },
    
    onSuccess: (data, variables, context) => {
      const { circuit, fixtureIds } = data;
      
      // Replace the optimistic circuit with the real one
      if (context?.optimisticCircuit) {
        const circuits = queryClient.getQueryData<Circuit[]>(
          queryKeys.entitiesByFloorPlan('circuits', variables.floorPlanId)
        );
        
        if (circuits) {
          const updatedCircuits = circuits.map(c => 
            c.id === context.optimisticCircuit.id ? circuit : c
          );
          queryClient.setQueryData(
            queryKeys.entitiesByFloorPlan('circuits', variables.floorPlanId),
            updatedCircuits
          );
        }
        
        // Update fixtures with the real circuit ID
        const fixtures = queryClient.getQueryData<LightingFixture[]>(
          queryKeys.entitiesByFloorPlan('lighting_fixtures', variables.floorPlanId)
        );
        
        if (fixtures && fixtureIds.length > 0) {
          const updatedFixtures = fixtures.map(fixture => 
            fixture.circuit_id === context.optimisticCircuit.id 
              ? { ...fixture, circuit_id: circuit.id }
              : fixture
          );
          queryClient.setQueryData(
            queryKeys.entitiesByFloorPlan('lighting_fixtures', variables.floorPlanId),
            updatedFixtures
          );
        }
      }
      
      toast.success('Load Successfully Created');
      logger.log('Circuit created successfully:', circuit.id);
    },
    
    onSettled: (data, error, variables) => {
      // Always invalidate to ensure consistency
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.entitiesByFloorPlan('circuits', variables.floorPlanId)
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.entitiesByFloorPlan('lighting_fixtures', variables.floorPlanId)
      });
    }
  });
}